const TAG_RE = /(@user:\d+|@cohort:\d+|@analysis:\d+|#[\w-]+)/g;

/**
 * Convert stored tag tokens in a message string to human-readable display text.
 * Used in contexts where chips are not rendered (feed, notification panel, edit textarea).
 *
 * @param {string} content        - raw message content
 * @param {Array}  channels       - channel list for the message's collection
 * @param {Object} usersById      - map of { [userId]: userName } (global, all collections)
 */
export default function resolveTagsToText(content, channels, usersById) {
  return content.replace(TAG_RE, raw => {
    if (raw.startsWith('@user:')) {
      const id = parseInt(raw.slice(6));
      return usersById[id] ? `@${usersById[id]}` : raw;
    }
    if (raw.startsWith('@cohort:')) {
      const id = parseInt(raw.slice(8));
      const ch = channels.find(c => c.type === 'cohort' && c.cohort_id === id);
      return ch ? `@${ch.cohort_label}` : raw;
    }
    if (raw.startsWith('@analysis:')) {
      const id = parseInt(raw.slice(10));
      const ch = channels.find(
        c => c.type === 'analysis' && c.analysis_id === id
      );
      return ch ? `@Analysis ${ch.analysis_num}` : raw;
    }
    // #channel-name — look up display name
    const name = raw.slice(1);
    const ch = channels.find(c => c.name === name);
    if (!ch) return raw;
    if (ch.type === 'cohort') return `#${ch.cohort_label}`;
    if (ch.type === 'analysis') return `#Analysis ${ch.analysis_num}`;
    return raw; // #general, #other-name already readable
  });
}

/**
 * Convert stored tag tokens to a normalized edit-form string that round-trips
 * correctly through preprocessContent() on save.
 *
 * Differences from resolveTagsToText:
 *  - Display names have non-alphanumeric chars stripped (matching what selectMentionItem inserts)
 *  - # channel tokens are left as-is (they're already stable and readable)
 *
 * @param {string} content        - raw message content
 * @param {Array}  channels       - channel list for the message's collection
 * @param {Object} usersById      - map of { [userId]: userName } (global, all collections)
 */
export function resolveTagsForEdit(content, channels, usersById) {
  const clean = s => s.replace(/[^a-zA-Z0-9]/g, '');
  return content.replace(TAG_RE, raw => {
    if (raw.startsWith('@user:')) {
      const id = parseInt(raw.slice(6));
      return usersById[id] ? `@${clean(usersById[id])}` : raw;
    }
    if (raw.startsWith('@cohort:')) {
      const id = parseInt(raw.slice(8));
      const ch = channels.find(c => c.type === 'cohort' && c.cohort_id === id);
      return ch ? `@${clean(ch.cohort_label)}` : raw;
    }
    if (raw.startsWith('@analysis:')) {
      const id = parseInt(raw.slice(10));
      const ch = channels.find(
        c => c.type === 'analysis' && c.analysis_id === id
      );
      return ch ? `@Analysis${ch.analysis_num}` : raw;
    }
    // #channel-name — look up display name (same as resolveTagsToText)
    const name = raw.slice(1);
    const ch = channels.find(c => c.name === name);
    if (!ch) return raw;
    if (ch.type === 'cohort') return `#${ch.cohort_label}`;
    if (ch.type === 'analysis') return `#Analysis ${ch.analysis_num}`;
    return raw; // #general, #other-name already readable
  });
}
